import type { IdentityCheck, ProductProfile } from "../../shared/goals/profile";
import type { RunState } from "../../shared/goals/runs";
import { post, request } from "./api";

/** One version of a product's profile, for its versions list. */
export interface ProfileVersion {
  version: number;
  state: string;
  by: "agent" | "owner";
  confirmed: boolean;
  question: boolean;
  at: string;
  note: string | null;
}

/** A product's profile as the API returns it: the confirmed version, the newest draft or question, and the learn run. */
export interface ProfileView {
  confirmed: { version: number; profile: ProductProfile; confirmedAt: string | null } | null;
  draft: { version: number; profile: ProductProfile | null; question: IdentityCheck | null; createdAt: string } | null;
  learnRun: { id: string; state: RunState; error: string | null } | null;
}

/** The owner's answer to "which business is yours?". */
export type LearnAnswer = { pick: number } | { website: string } | { none: true };

export interface ProductGoal {
  id: string;
  sentence: string;
  state: string;
  version: number;
  created_at: string;
  launched_at: string | null;
  ended_at: string | null;
  /** The goal's results so far, or null before any are counted. */
  results: { results: number; spendCents: number; clicks: number; asOf: string | null } | null;
  /** Where the goal's Meta ads open, and whether that page's tracking is checked; null without Meta ads. */
  opens: { opens: "site" | "metaform"; page: string | null; tracking: "checked" | "not" | "built_in" } | null;
}

export interface RunInfo {
  id: string;
  product_id: string | null;
  goal_id: string | null;
  kind: string;
  state: RunState;
  cost_ai_micros: number;
  cost_data_micros: number;
  cost_media_micros: number;
  error: string | null;
  /** What the run saved, once it ended: `{"result":{"tool":"save_playbook","id":"lib_..."}}`. */
  summary_json?: string | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface RunEvent {
  seq: number;
  at: string;
  type: string;
  data: Record<string, unknown>;
}

export const productsApi = {
  profile: (id: string) => request<ProfileView>(`/products/${encodeURIComponent(id)}/profile`),
  learn: (id: string, body: { note?: string; answer?: LearnAnswer }) =>
    post<{ runId: string }>(`/products/${encodeURIComponent(id)}/learn`, body),
  /** Confirm the latest draft, or change the latest confirmed version; `profile` is the owner's corrected copy, saved as the next version. */
  confirm: (id: string, body: { version: number; profile?: ProductProfile }) =>
    post<{ version: number }>(`/products/${encodeURIComponent(id)}/profile/confirm`, body),
  goals: (id: string) => request<{ goals: ProductGoal[] }>(`/products/${encodeURIComponent(id)}/goals`),
  versions: (id: string) => request<{ versions: ProfileVersion[] }>(`/products/${encodeURIComponent(id)}/profile/versions`),
  /** One version's words; null for a draft that asked which business is theirs. */
  version: (id: string, n: number) => request<{ profile: ProductProfile | null }>(`/products/${encodeURIComponent(id)}/profile/versions/${n}`),
  /** Use an older version's words: the next version, confirmed, on top of `onto`. */
  useVersion: (id: string, n: number, onto: number) => post<{ version: number }>(`/products/${encodeURIComponent(id)}/profile/versions/${n}/use`, { onto }),
};

export const runsApi = {
  get: (id: string) => request<{ run: RunInfo }>(`/runs/${encodeURIComponent(id)}`),
  events: (id: string, after: number) => request<{ events: RunEvent[] }>(`/runs/${encodeURIComponent(id)}/events?after=${after}`),
};

/** A run that may still change: the agent has it, or will pick it up again. */
export const runActive = (state: RunState) => state === "queued" || state === "running" || state === "interrupted";
